"use client";

import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import {
  Mail,
  MapPin,
  Phone,
  Facebook,
  Instagram,
  Youtube,
  Linkedin,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

export default function ContactPage() {
  const sectionRef = useRef(null);
  const formRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({ name: false, email: false, message: false });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".fade-in", {
        opacity: 0,
        y: 50,
        duration: 1,
        stagger: 0.2,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
        },
      });

      gsap.from(formRef.current, {
        opacity: 0,
        x: 100,
        duration: 1,
        scrollTrigger: {
          trigger: formRef.current,
          start: "top 80%",
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const validateEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = {
      name: form.name.trim() === "",
      email: !validateEmail(form.email),
      message: form.message.trim() === "",
    };

    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) return;

    setSubmitted(true);
    const mailto = `mailto:your@email.com?subject=Message from ${form.name}&body=${form.message} (${form.email})`;
    window.location.href = mailto;
  };

  const renderInput = (
    type: "text" | "email" | "textarea",
    name: keyof typeof form,
    placeholder: string,
    errorMessage: string
  ) => {
    const value = form[name];
    const error = errors[name];

    const commonProps = {
      name,
      value,
      onChange: handleChange,
      placeholder,
      className: `w-full px-4 py-3 border outline-none focus:ring-2 ${
        error ? "border-red-500 ring-red-200" : "border-gray-300 focus:ring-primary"
      }`,
    };

    return (
      <div className="fade-in">
        {type === "textarea" ? (
          <textarea {...commonProps} rows={4} className={`${commonProps.className} rounded-2xl resize-none`} />
        ) : (
          <input {...commonProps} type={type} className={`${commonProps.className} rounded-full`} />
        )}
        {error && <p className="text-red-500 text-sm mt-1">{errorMessage}</p>}
      </div>
    );
  };

  return (
    <section id="contact" ref={sectionRef} data-scroll-section className="px-4 py-16 bg-white flex items-center justify-center">
      <div className="w-full max-w-6xl grid grid-cols-1 md:grid-cols-2 bg-primary shadow-xl rounded-2xl overflow-hidden">
        
        {/* Left Side */}
        <div className="bg-primary text-white p-8 md:p-12 flex flex-col justify-between">
          <div className="fade-in">
            <h2 className="text-3xl font-bold mb-4">Get In Touch</h2>
            <p className="text-sm mb-8">
              Jangan ragu untuk menghubungi kami! Kirimkan pertanyaan Anda di sini dan kami akan menghubungi Anda sesegera mungkin.
            </p>
            <div className="space-y-5 text-sm">
              {/* <div className="flex items-center gap-3">
                <Phone size={18} />
                <span>470-601-1911</span>
              </div> */}
              <div className="flex items-center gap-3">
                <Mail size={18} />
                <span>contact@geniusakademi.com</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin size={18} />
                <span> Kantor 1 : Jl. Bromo 181 Triwung Kidul 
                  <br/> Kecamatan Kademangan Kota Probolinggo 67224
                  </span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin size={18} />
                <span>
                  Kantor 2 : Dusun Pasar Sambek Desa Kropak 
                  <br/> Kecamatan Bantaran Kabupaten Probolinggo 67261
                </span>
              </div>
            </div>
          </div>
          {/* <div className="flex gap-4 mt-8 text-white text-xl fade-in">
            <Facebook className="hover:text-blue-300 cursor-pointer" />
            <Instagram className="hover:text-pink-300 cursor-pointer" />
            <Linkedin className="hover:text-blue-400 cursor-pointer" />
            <Youtube className="hover:text-red-400 cursor-pointer" />
          </div> */}
        </div>

        {/* Right Side - Form */}
        <div
          ref={formRef}
          className="bg-white p-8 md:p-12 text-gray-800"
        >
          <h3 className="text-2xl font-semibold mb-6 fade-in">Hubungi kami</h3>
          <form onSubmit={handleSubmit} className="space-y-5">
            {renderInput("text", "name", "Name", "Name is required")}
            {renderInput("email", "email", "Email", "Invalid email address")}
            {renderInput("textarea", "message", "Message", "Message is required")}

            <motion.button
              whileTap={{ scale: 0.95 }}
              type="submit"
              className="w-full bg-primary text-white font-semibold py-3 rounded-full hover:bg-primary/90 transition"
            >
              Submit
            </motion.button>

            {submitted && (
              <motion.p
                className="text-primary mt-2 text-sm text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                Message submitted! We'll get back to you soon.
              </motion.p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
